import { logger, setTraceContextGetter } from '../logger/logger';

// Trace context storage
let currentTraceContext: { traceId?: string; spanId?: string } = {};

/**
 * Parse traceparent header (W3C Trace Context format)
 * Format: version-trace_id-parent_id-trace_flags
 */
function parseTraceParent(traceparent: string): { traceId: string; spanId: string } | null {
  try {
    const parts = traceparent.split('-');
    if (parts.length !== 4 || parts[0] !== '00') {
      return null;
    }

    const traceId = parts[1];
    const spanId = parts[2];

    // Validate format
    if (!traceId || !spanId || !/^[0-9a-f]{32}$/i.test(traceId) || !/^[0-9a-f]{16}$/i.test(spanId)) {
      return null;
    }

    return { traceId, spanId };
  } catch {
    return null;
  }
}

/**
 * Generate a new trace ID (32 hex characters)
 */
function generateTraceId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Generate a new span ID (16 hex characters)
 */
function generateSpanId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Extract trace context from fetch response headers or generate new
 */
export function extractTraceContextFromResponse(response: Response): void {
  const traceparent = response.headers.get('traceparent');
  if (traceparent) {
    const parsed = parseTraceParent(traceparent);
    if (parsed) {
      currentTraceContext = parsed;
      return;
    }
  }

  // Try custom headers
  const traceId = response.headers.get('x-trace-id');
  const spanId = response.headers.get('x-span-id');

  if (traceId && spanId) {
    currentTraceContext = { traceId, spanId };
    return;
  }
}

/**
 * Initialize trace context (call on app start or page load)
 */
export function initTraceContext(): void {
  // Try to get trace context from sessionStorage (persist across page reloads)
  const stored = sessionStorage.getItem('trace_context');
  if (stored) {
    try {
      const parsedRaw = JSON.parse(stored) as unknown;
      const parsed = typeof parsedRaw === 'object' && parsedRaw !== null ? parsedRaw as Record<string, unknown> : null;
      if (
        parsed !== null &&
        'traceId' in parsed &&
        'spanId' in parsed &&
        typeof parsed['traceId'] === 'string' &&
        typeof parsed['spanId'] === 'string'
      ) {
        currentTraceContext = {
          traceId: parsed['traceId'],
          spanId: parsed['spanId'],
        };
        return;
      }
    } catch {
      // Invalid stored data, generate new
    }
  }

  // Generate new trace context
  currentTraceContext = {
    traceId: generateTraceId(),
    spanId: generateSpanId(),
  };

  // Store in sessionStorage
  try {
    sessionStorage.setItem('trace_context', JSON.stringify(currentTraceContext));
  } catch {
    // SessionStorage not available, continue without persistence
  }
}

/**
 * Get current trace context (for logger integration)
 */
export function getTraceContext(): { traceId?: string; spanId?: string } {
  return currentTraceContext;
}

/**
 * Set trace context manually (useful for testing)
 */
export function setTraceContext(traceId: string, spanId: string): void {
  currentTraceContext = { traceId, spanId };
}

/**
 * Create traceparent header value for outgoing requests
 */
export function createTraceParent(): string {
  const { traceId, spanId } = currentTraceContext;
  if (!traceId || !spanId) {
    // Generate new context if missing
    initTraceContext();
    const newContext = currentTraceContext;
    return `00-${newContext.traceId}-${newContext.spanId}-01`;
  }
  return `00-${traceId}-${spanId}-01`;
}

/**
 * Initialize OpenTelemetry integration
 * Call this early in the app lifecycle (e.g., in main.tsx)
 */
export function initOtel(): void {
  // Initialize trace context
  initTraceContext();

  // Register trace context getter with logger
  setTraceContextGetter(getTraceContext);

  // Log initialization (only in development)
  if (import.meta.env.MODE !== 'production') {
    const otlpUrlRaw = import.meta.env['VITE_OTLP_LOGS_URL'] as unknown;
    const otlpUrl = typeof otlpUrlRaw === 'string' ? otlpUrlRaw : undefined;
    logger.info('OpenTelemetry initialized', {
      component: 'browser-otel',
      action: 'initialization',
      trace_id: currentTraceContext.traceId,
      span_id: currentTraceContext.spanId,
      otlp_logs_url: otlpUrl || 'not configured',
    });
  }
}
